import React, { useState, useEffect, useContext, useCallback } from "react"
import clsx from "clsx"
import formatValue from "../utils/formatValue"
import Skeleton from '@material-ui/lab/Skeleton'
import { Box, CircularProgress, useTheme, Checkbox, Chip, Box as MuiBox } from "@material-ui/core"
import {
  List,
  Card,
  CardTitle,
  CardSubtitle,
  CardBody,
  CardMedia,
  RichText,
  Text,
  CardFooter,
  FileListView,
  AvatarInitial
} from "."
import useStyles from './styles'
import { useAppContext } from "App"
import { getStyleProgressStatus, getCustomTextStyle, resolveColor } from "shared/utils"
import moment from "moment"
import activityTypes from "fixtures/activityTypes"
import {
  DirectionsRun as ActivityIcon,
  DoubleArrow as PriorityIcon,
  PriorityHighRounded as CriticalIcon,
  Block as BlockerIcon,
  ScheduleSharp as ScheduledIcon,
} from '@material-ui/icons'

export const CardList = React.memo((props) => {
  const classes = useStyles()
  const theme = useTheme()
  const appContext = useAppContext()
  const { fields, skeleton = 0, renderCardItem, itemListRef, items, selectedIndex, onCardClick, variant, isFetching, ...listProps } = props
  // destructure selection props
  const { showCheckBox, onRowSelect, isRowSelected } = props

  const [hoveredIndex, setHoveredIndex] = useState(null)

  const onCardItemClick = useCallback((item) => () => onCardClick && onCardClick(item),
    [onCardClick])

  const renderFieldComponent = useCallback((item, field) => {
    if (field.component == 'ActivityInfo') {
      const activityInfoList = [
        {
          key: 'scheduled_activities_count',
          icon: ScheduledIcon,
          color: '#ff6f00'
        },
        {
          key: 'activities_count',
          icon: ActivityIcon,
          color: '#aaa'
        },
        {
          key: 'comments_count',
          icon: (activityTypes['chat'] || {}).icon,
          color: '#aaa'
        }
      ]

      return (
        <div style={{ display: 'flex', flexDirection: 'row' }}>
          {
            activityInfoList
              // ?.filter(f => typeof item[f.key] !== 'undefined')
              ?.filter(f => item[f.key])
              ?.map(act => (
                <div className={classes.activityItem}>
                  {React.createElement(
                    act.icon, {
                    style: { color: act.color, fontSize: '15px', marginRight: '2px' }
                  }
                  )}
                  <Text variant="subtitle2">
                    {item[act.key]}
                  </Text>
                </div>
              ))
          }
        </div>
      )
    }

    return ''
  }, [])

  const getUserName = (user) => {
    if (!user) return ''

    let name = ''
    if (user) {
      if (user?.first_name || user?.last_name) {
        name = `${user?.first_name || ""} ${user?.last_name || ""}`.trim()
      }
    }

    if (!name) {
      name = `Contact ${user?.uid % 9999}`
    }

    return name
  }

  const getFieldType = (type) => {
    const types = ['resolution', 'issue_type']
    if (types.some(t => t === type))
      return 'option'

    return type
  }

  const getPriorityStyle = (key) => {
    let style = {}
    const base = { display: 'inline-block', position: 'relative' }

    if (key === 'high') {
      style = { ...base, color: '#E57373', transform: 'rotate(270deg)' }
    } else if (key === 'low') {
      style = { ...base, color: 'green', transform: 'rotate(90deg)', marginLeft: -2, marginRight: 4 }
    } else if (key === 'trivial') {
      style = { ...base, color: 'grey', transform: 'rotate(90deg)', marginLeft: -2, marginRight: 4 }
    } else if (['blocker', 'critical'].includes(key)) {
      style = { ...base, color: '#B71C1C', marginRight: 4 }
    }
    // else if (['blocker'].includes(key)) {
    //   style = { ...base, color: 'red', marginRight: 4 }
    // } 
    else {
      style = { ...base, top: 4, color: 'rgb(255, 179, 0)', marginRight: 2 }
    }

    return style
  }

  const getFieldPadding = useCallback((type, key) => {
    if (getFieldType(type) === 'user' && key === 'assigned_to') {
      return '3px'
    } else if (getFieldType(type) === 'richtext') {
      return '3px'
    } else return 0
  }, [])

  const getFieldData = useCallback((item, field, selected) => {
    let fieldValue = null

    if (!field)
      return null
    else if (field.render)
      fieldValue = field.render(item, field)
    else if (field.component)
      fieldValue = renderFieldComponent(item, field)
    else if (getFieldType(field.field_type) == 'contact') {
      fieldValue = getUserName(item[field.key])
    }
    else if (getFieldType(field.field_type) == 'company')
      fieldValue = item[field.key]?.name
    else if (field?.field_type == 'company_contact')
      fieldValue = `${getUserName(item[field.key]?.contact)} ${item[field.key]?.company ? `from ${item[field.key]?.company?.name}` : ''}`
    else if (getFieldType(field.field_type) == 'option') {
      if (field?.multiple_value) {
        fieldValue = item[field.key]?.map(field => <Chip label={field.name} style={{ margin: '3px 5px 3px 0' }} />)
      } else fieldValue = item[field.key]?.name
    }
    else if (getFieldType(field.field_type) == 'custom' && typeof item?.[field.key] == 'object') {
      fieldValue = item?.[field.key]?.name || item?.[field.key]?.title || item?.[field.key]?.value || ''
    }
    else if (getFieldType(field.field_type) == 'priority') {
      let priorityValue = ''
      if (field?.multiple_value) {
        priorityValue = item[field.key]?.map(field => <Chip label={field.name} style={{ margin: '3px 5px 3px 0' }} />)
      } else priorityValue = item[field.key]?.name

      fieldValue = <span style={{ display: 'inline-flex', alignItems: 'baseline' }}>
        {['normal', 'low', 'high', 'trivial'].includes(item[field.key]?.key) && (
          <div style={getPriorityStyle(item[field.key]?.key)}>
            <PriorityIcon fontSize="inherit" />
          </div>
        )}
        {item[field.key]?.key === 'critical' && (
          <div style={{ ...getPriorityStyle(item[field.key]?.key), fontSize: '0.7rem' }}>
            ‼️
          </div>
        )}
        {item[field.key]?.key === 'blocker' && (
          <div style={{ ...getPriorityStyle(item[field.key]?.key), fontSize: '0.9rem' }}>
            🚫
          </div>
        )}
        {priorityValue}
      </span>
    }
    else if (getFieldType(field.field_type) == 'user') {
      let agentName = ''
      if (item[field.key]) {
        if (item[field.key].uid == appContext.userInfo?.uid)
          agentName = "Me"
        else if (Array.isArray(item[field.key]))
          agentName = item[field.key].map(i => `${i.first_name || ''} ${i.last_name || ''}`).join(', ')
        else
          agentName = `${item[field.key].first_name || ''} ${item[field.key].last_name || ''}`

        fieldValue = field.key === 'assigned_to' ? (
          <span style={{ display: 'inline-flex', alignItems: 'baseline' }}>
            <div style={{ display: 'inline-block', position: 'relative', top: '-1.5px', marginRight: 4 }}>
              <AvatarInitial user={item[field.key]} size="xs" />
            </div>
            {agentName}
          </span>
        ) : agentName
      } else {
        if (field.key === 'assigned_to') {
          /** Assigned to all agent */
          fieldValue = "Unassigned"
        } else {
          /** Hide if empty */
          fieldValue = null
        }
      }
    }
    else if (getFieldType(field.field_type) == 'status') {
      fieldValue = (
        <div style={{ display: 'flex' }}>
          <MuiBox>
            <Text
              variant="body2"
              noWrap={true}
              textCustomProps={getStyleProgressStatus(item[field.key])}
            >
              {item[field.key]?.name}
            </Text>
          </MuiBox>
        </div>
      )
    }
    else if (getFieldType(field.field_type) == 'float')
      fieldValue = item[field.key] ? new Intl.NumberFormat('ID').format(item[field.key]) : ''
    else if (
      getFieldType(field.field_type) == 'date' ||
      (moment(item[field.key], moment.ISO_8601, true).isValid() && !field.format) // check value is date without format
    )
      fieldValue = formatValue(item[field.key], 'datetime|DD MMM yyy')
    else if (getFieldType(field.field_type) == 'media')
      fieldValue = item[field.key] ? (
        <FileListView value={item[field.key]} useSmallThumbs disableBorder />
      ) : <></>
    else if (getFieldType(field.field_type) == 'time') {
      fieldValue = (
        <div style={{ display: 'flex' }}>
          <Text variant="body2" noWrap={true} textCustomProps={field?.text_props || {}}>
            {moment(item[field.key]).format('HH:mm')}
          </Text>
        </div>
      )
    } else if (getFieldType(field.field_type) == 'time_range_start') {
      const timeRangeEndField = fields?.body?.find(f => f?.field_type === 'time_range_end')?.key
      fieldValue = (
        <div style={{ display: 'flex' }}>
          <Text variant="body2" noWrap={true} textCustomProps={field?.text_props || {}}>
            {moment(item[field.key]).format('HH:mm')}{timeRangeEndField ? ` - ${moment(item[timeRangeEndField]).format('HH:mm')}` : ''}
          </Text>
        </div>
      )
    } else if (getFieldType(field.field_type) == 'time_range_end') {
      /** hide time_range_end */
      fieldValue = null
    } else if (getFieldType(field.field_type) === 'richtext') {
      fieldValue = item[field.key]?.length > 0 ? (
        <Text
          variant="body2"
          lineCount={4}
          maxLine={4}
          textCustomProps={{ whiteSpace: 'pre-line' }}>
          {item[field.key]}
        </Text>
      ) : null
    } else if (getFieldType(field.field_type) == 'issue') {
      if (item[field.key]) {
        const items = Array.isArray(item[field.key]) ? item[field.key] : [item[field.key]]
        fieldValue = items?.map(field => <Chip label={field.key} style={{ margin: '3px 5px 3px 0' }} />)
      } else fieldValue = null
    } else fieldValue = formatValue(item[field.key], field.format)

    /** set custom color */
    if (['string', 'number'].includes(typeof fieldValue) && field.field_type !== 'status') {
      const hasAnyColor = !!(
        resolveColor(field.color, fieldValue) ||
        resolveColor(field.bg_color, fieldValue)
      )

      if (hasAnyColor) {
        const valueHolder = fieldValue
        fieldValue = (
          <span style={getCustomTextStyle(field?.color, field?.bg_color, valueHolder)}>
            {valueHolder}
          </span>
        )
      }
    }

    return (field?.label && fieldValue) ? (
      <div style={{ paddingTop: getFieldPadding(field.field_type, field.key) }}>
        <Text variant="body2" lineCount={getFieldType(field.field_type) === 'issue' ? 4 : undefined} maxLine={getFieldType(field.field_type) === 'issue' ? 4 : undefined}>
          {field.label}: {fieldValue}
        </Text>
      </div>
    ) : (
      <div style={{ paddingTop: getFieldPadding(field.field_type, field.key) }}>
        {fieldValue}
      </div>
    )
  }, [appContext?.userInfo?.uid, fields, getFieldPadding, renderFieldComponent])

  // const isIssueDone = (item) => item?.status?.progress >= 90 && item?.status?.progress <= 100
  const isIssueDone = useCallback((item) => item?.status?.progress >= 90 && item?.status?.progress <= 100, [])

  function renderCardSkeleton(index) {
    if (renderCardItem)
      return renderCardItem(undefined, index, { skeleton: 1 })
    else
      return (
        <Card variant={variant} skeleton={1}>
          {fields.title &&
            <CardTitle
              primary={<Skeleton />}
            />
          }
          {fields.subtitle &&
            <CardSubtitle
              primary={<Skeleton />}
            />
          }
          {fields.body &&
            <CardBody
              rows={fields.body?.map(field => <Skeleton />)}
            />
          }
        </Card>
      )
  }

  const renderCard = useCallback((item, index) => {
    const draggableId = listProps.droppableId ? `${listProps.droppableId}-${index}` : undefined
    if (renderCardItem)
      return renderCardItem(item, index, {
        variant: variant,
        selected: index == selectedIndex,
        index: index,
        onClick: onCardItemClick(item),
        draggableId,
        onMouseEnter: () => setHoveredIndex(index),
        onMouseLeave: () => setHoveredIndex(null),
        style: (hoveredIndex === index) && (index !== selectedIndex)
          ? { backgroundColor: 'rgba(0,0,0,0.04)', borderRadius: theme.shape.borderRadius }
          : {},
      })
    else
      return (
        <div className={classes.selectableCardWrapper}>
          {showCheckBox && (
            <div className={classes.listCheckbox}>
              <Checkbox
                checked={isRowSelected && isRowSelected(item, index)}
                onChange={(event) => onRowSelect && onRowSelect(item, index)}
              />
            </div>
          )}
          <Card
            variant={variant} onClick={onCardItemClick(item)}
            key={index}
            selected={index == selectedIndex}
            index={index}
            draggableId={draggableId}
            leftItemRender={fields?.leftItemRender ? () => fields.leftItemRender(item, index) : undefined}
          >
            {fields.media &&
              <CardMedia
                selected={index == selectedIndex}
                media={getFieldData(item, fields.media)}
                width={fields.media.width}
                aspectRatio={fields.media.aspectRatio}
              />
            }
            {fields.title &&
              <CardTitle
                selected={index == selectedIndex}
                textPrimaryColor={isIssueDone(item) ? "textSecondary" : undefined}
                primary={getFieldData(item, fields.title.primary)}
                secondary={getFieldData(item, fields.title.secondary)}
              />
            }
            {fields.subtitle &&
              <CardSubtitle
                selected={index == selectedIndex}
                maxLine={3}
                primary={getFieldData(item, fields.subtitle.primary)}
                textPrimaryColor={isIssueDone(item) ? "textSecondary" : undefined}
                secondary={getFieldData(item, fields.subtitle.secondary)}
              />
            }
            {fields.body &&
              <CardBody
                selected={index == selectedIndex}
                rows={fields.body?.map(field => getFieldData(item, field, index === selectedIndex))}
              />
            }
            {fields.footer && Object.keys(fields.footer).length > 0 &&
              <CardFooter
                selected={index == selectedIndex}
                maxLine={3}
                primary={getFieldData(item, fields.footer.primary)}
                textPrimaryColor={isIssueDone(item) ? "textSecondary" : undefined}
                secondary={getFieldData(item, fields.footer.secondary)}
              />
            }
          </Card>
        </div>
      )
  }, [renderCardItem, variant, selectedIndex, onCardItemClick, hoveredIndex, theme])

  return (
    <List variant={variant} {...listProps}>
      {skeleton ?
        [...Array(skeleton)].map((_, index) => renderCardSkeleton(index))
        : items
          .map((item, index) => (
            <div ref={ref => { if (itemListRef) itemListRef.current[index] = ref }}>
              {renderCard(item, index)}
            </div>
          ))
          .reduce((p, n) => p ?
            [...p, <div className={variant === "card" && classes.cardSeparator} />, n] :
            [n], null)
      }

      {isFetching &&
        <Box display='flex' justifyContent='center' style={{ padding: theme.spacing(2) }}>
          <CircularProgress size='1.5rem' disableShrink={true} />
        </Box>
      }
    </List>
  )
}, (prevProps, nextProps) => {
  return (
    prevProps.items === nextProps.items &&
    prevProps.skeleton === nextProps.skeleton &&
    prevProps.selectedIndex === nextProps.selectedIndex &&
    prevProps.isFetching === nextProps.isFetching &&
    prevProps.fields === nextProps.fields
  )
})

export default CardList